/**
 * System Settings Page
 * 
 * Admin-only page for household and system settings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../../contexts/FamilyAuthContext';
import axiosClient from '../../../api/axiosClient';

function SystemSettings() {
  const { isAdmin, household, familyUser } = useFamilyAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('household');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Household data
  const [householdData, setHouseholdData] = useState(null);
  const [devices, setDevices] = useState([]);
  
  useEffect(() => {
    if (!isAdmin) {
      navigate('/family/dashboard');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch household info using cookie-based admin endpoint
      const hhResponse = await axiosClient.get('/api/admin/household');
      setHouseholdData(hhResponse.data);
      
      // Fetch devices using cookie-based admin endpoint
      const devicesResponse = await axiosClient.get('/api/admin/devices');
      setDevices(devicesResponse.data || []);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHousehold = async () => {
    try {
      setSaving(true);
      await axiosClient.put('/api/admin/household', householdData);
      alert('Einstellungen gespeichert');
    } catch (err) {
      alert('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#5B4B8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Lade Einstellungen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      {/* Header */}
      <div className="bg-[#0D1124]/90 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/family/dashboard')}
              className="p-2 hover:bg-neutral-700/50 rounded-lg transition-colors"
            >
              ← Zurück
            </button>
            <div>
              <h1 className="text-2xl font-bold">Systemeinstellungen</h1>
              <p className="text-sm text-neutral-400">Haushalt und Geräte verwalten</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['household', 'devices'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-gradient-to-r from-[#5B4B8A] to-[#6DCEE8] text-white'
                  : 'bg-neutral-700/50 hover:bg-neutral-700/70 text-neutral-300'
              }`}
            >
              {tab === 'household' && '🏠 Haushalt'}
              {tab === 'devices' && '📱 Geräte'}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'household' && (
          <div className="bg-[#0D1124]/80 backdrop-blur-xl rounded-2xl border border-neutral-600/40 p-6">
            <h2 className="text-xl font-semibold mb-6">Haushaltsinformationen</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={householdData?.name || ''}
                  onChange={(e) => setHouseholdData({ ...householdData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-[#050816] border border-neutral-600/40 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#5B4B8A]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Erstellt am</label>
                <input
                  type="text"
                  value={new Date(householdData?.created_at).toLocaleDateString('de-DE')}
                  disabled
                  className="w-full px-4 py-3 bg-neutral-700/30 border border-neutral-600/40 rounded-xl text-neutral-400"
                />
              </div>
              <button
                onClick={handleSaveHousehold}
                disabled={saving}
                className="px-6 py-3 bg-gradient-to-r from-[#5B4B8A] to-[#6DCEE8] hover:opacity-90 rounded-xl font-semibold transition-all disabled:opacity-50"
              >
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'devices' && (
          <div className="bg-[#0D1124]/80 backdrop-blur-xl rounded-2xl border border-neutral-600/40 p-6">
            <h2 className="text-xl font-semibold mb-6">Geräte ({devices.length})</h2>
            <div className="space-y-4">
              {devices.map((device) => {
                // Use consistent status logic from Dashboard
                const isPaired = device.status === 'paired' && device.senior_id;
                const isOnline = device.connection_state === 'online';
                
                // Determine status badge
                let statusBadge = { text: 'Nicht gekoppelt', color: 'text-neutral-400', icon: '⚪' };
                if (isPaired && isOnline) {
                  statusBadge = { text: 'Verbunden & Online', color: 'text-green-400', icon: '🟢' };
                } else if (isPaired) {
                  statusBadge = { text: 'Gekoppelt, Offline', color: 'text-blue-400', icon: '🔵' };
                }
                
                // Get device name
                const deviceName = device.device_name || device.name || device.device_id || 'Unbenanntes Gerät';
                
                // Format last active time
                const lastActive = device.last_active 
                  ? new Date(device.last_active).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'Nie';
                
                return (
                  <div key={device.id} className="p-4 bg-neutral-700/30 rounded-xl">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-white mb-2">{deviceName}</h3>
                        
                        {/* Status Badge */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-sm font-medium ${statusBadge.color}`}>
                            {statusBadge.icon} {statusBadge.text}
                          </span>
                        </div>
                        
                        {/* Senior Assignment */}
                        {device.senior_id && (
                          <p className="text-sm text-neutral-400 mb-1">
                            <span className="font-medium">Senior:</span> {device.senior_name || device.senior_id}
                          </p>
                        )}
                        
                        {/* Last Active */}
                        <p className="text-xs text-neutral-500">
                          Zuletzt aktiv: {lastActive}
                        </p>
                        
                        {/* Device UUID (for debugging) */}
                        {device.device_uuid && (
                          <p className="text-xs text-neutral-600 mt-1 font-mono">
                            UUID: {device.device_uuid.slice(0, 8)}...
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {devices.length === 0 && (
                <p className="text-center text-neutral-400 py-8">
                  Keine Geräte vorhanden
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SystemSettings;
